/**
 * Shared themed page-shell atoms for the generic-CRUD admin pages (OIDCPAM
 * Gate-6 Phase 4a: Clients/Apps/Resources; reused by later Gate-6 phases for
 * Connectors/Users/Subjects/Tenants/Entitlements/Policies per the shared
 * constraints doc's Phase-4 directive). All three initial pages share the
 * exact same shell — page title, "All X" section (table), optional "Edit X"
 * section (form), "Create X" section (form), and a tenant-not-selected
 * gate — so this file factors that shell out of the per-page MUI
 * Box/Typography usage instead of duplicating it three times.
 *
 * Heading levels: AdminLayout's <Brand> is the page's only <h1> ("OIDC
 * Admin"), so PageTitle renders <h2> (page name, e.g. "OAuth Clients") and
 * SectionTitle renders <h3> ("All Clients" / "Edit Client" / "Create
 * Client") — a coherent hierarchy under the shell's h1, improving on the
 * pre-migration MUI Typography variant="h5"/"h6" (which mapped to literal
 * <h5>/<h6> tags with no h2-h4 in between).
 */
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'

/** Top-level vertical page layout (replaces `Box sx={{display:'flex',flexDirection:'column',gap:3}}`). */
export const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

export const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.lg ?? '1.5rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

/**
 * A single named section (the "All X" table, "Edit X" form, "Create X"
 * form). Deliberately a plain `<div>` (not `<section>`) — pages/tests locate
 * a section by finding its heading text and calling `.closest('div')`.
 */
export const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.md ?? '1.125rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

/** "Please select a tenant to manage X" gate copy. */
export const TenantGateText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`
